import { definePlugin } from '../../framework';
import type { FieldSuggestion, TransformStep } from '../../types';
import {
  confirmation,
  fieldExpectation,
  type FieldExpectation,
} from '../../utils/confirmation';

// Every kind of separator a compound value is built with, structural ones
// first so their split wins when the same part is found twice. A part is only
// offered when something confirms it (see findSplitCandidates), so splitting
// on spaces or dashes doesn't produce guesses.
const SEPARATORS = ['/', ',', '.', ';', '|', '-', ' '];
const MAX_SEGMENTS = 6;

const isWrappedSnippet = (snippet: unknown): snippet is [unknown[]] =>
  Array.isArray(snippet) &&
  Array.isArray(snippet[0]) &&
  snippet[0].length > 0;

export function buildSplitSnippet(originalSnippet: unknown, delimiter: string, index: number) {
  return {
    $declareVariable: {
      original: originalSnippet,
      part: {
        '$original.split': delimiter,
        $result: { $getVariable: `data.${index}` },
      },
    },
    $getVariable: 'part',
  };
}

function splitAllSegments(value: string, delimiter: string): string[] | null {
  const segments = value.split(delimiter).map((s) => s.trim());
  if (segments.length < 2 || segments.length > MAX_SEGMENTS) return null;
  if (segments.some((s) => s.length === 0)) return null;
  return segments;
}

/**
 * The non-empty parts of a value split on one separator, each with its index
 * in the raw split — the index the engine's split gives it, empty pieces
 * included, so "A  B" split on a space finds "B" at index 2.
 */
export function splitParts(value: string, delimiter: string): SplitPart[] | null {
  const parts = value
    .split(delimiter)
    .map((raw, index) => ({ index, part: raw.trim() }))
    .filter(({ part }) => part.length > 0);
  if (parts.length < 2 || parts.length > MAX_SEGMENTS) return null;
  return parts;
}

function looksLikeFormattedNumber(value: string): boolean {
  const match = value.trim().match(/^([\d.,]+)\s*([a-zA-Z%€$£]{0,6})?$/);
  if (!match) return false;
  const numericPart = match[1];
  if (!/\d/.test(numericPart)) return false;
  if (/[.,]{2,}/.test(numericPart)) return false;
  if (/^[.,]|[.,]$/.test(numericPart)) return false;
  return true;
}

function looksLikeDateOrTime(value: string): boolean {
  const datePattern = /\b\d{1,4}[.\-/]\d{1,2}[.\-/]\d{1,4}\b/;
  const timePattern = /\b\d{1,2}:\d{2}(:\d{2})?\b/;
  return datePattern.test(value) || timePattern.test(value);
}

function looksLikeDateFormatPattern(value: string): boolean {
  const tokens = value.trim().split(/[^a-zA-Z]+/).filter(Boolean);
  if (tokens.length === 0) return false;
  return tokens.every((token) => /^([YMDHhms])\1*$/.test(token));
}

/** A code with both letters and digits, e.g. a container type like "4HDC" or "22G1". */
const CODE_TOKEN = /^(?=.*\d)(?=.*[A-Za-z])[A-Za-z0-9#'-]+$/;
const isPlainNumber = (s: string) => /^[\d.,]+$/.test(s) && looksLikeFormattedNumber(s);

type SplitPart = { index: number; part: string };

/**
 * A code and a number stuck together in one value, e.g. "4HDC 3,700"
 * (container type and tare): exactly two parts separated by whitespace, one
 * a plain number, the other a code with letters and digits. Values that are
 * just words ("ROTTERDAM NETHERLANDS", "Havennummer 8970") don't qualify.
 */
export function findCodeNumberPair(
  value: string,
): { delimiter: string; code: SplitPart; number: SplitPart } | null {
  const match = value.trim().match(/^(\S+)(\s+)(\S+)$/);
  if (!match) return null;
  const [, first, delimiter, second] = match;
  if (CODE_TOKEN.test(first) && isPlainNumber(second)) {
    return { delimiter, code: { index: 0, part: first }, number: { index: 1, part: second } };
  }
  if (isPlainNumber(first) && CODE_TOKEN.test(second)) {
    return { delimiter, code: { index: 1, part: second }, number: { index: 0, part: first } };
  }
  return null;
}

export { fieldExpectation, type FieldExpectation };

export type SplitCandidate = { delimiter: string; index: number; part: string };

/**
 * Parts of a compound value that could be the field's value. A value is split
 * on every kind of separator, and a part is offered only when something
 * confirms it:
 *   - it is the value the plugin expected (e.g. the LLM's reading),
 *   - another suggestion has exactly that value,
 *   - the field only accepts certain values and this is one of them, while
 *     the whole value isn't,
 *   - the field holds a number and this is the only plain number in it.
 * A `label: value` takes the value, and a code stuck to a number gives a
 * numeric field the number.
 */
export function findSplitCandidates(
  suggestion: FieldSuggestion,
  others: FieldSuggestion[],
  field: FieldExpectation = {},
): SplitCandidate[] {
  if (!isSplittable(suggestion)) return [];
  const value = suggestion.value as string;

  // "Label: value" takes the value. A label is words, not numbers: in
  // "40' Container H: 8'6''" the colon belongs to the value itself.
  const colonSegments = splitAllSegments(value, ':');
  if (colonSegments && colonSegments.slice(0, -1).every((label) => !/\d/.test(label))) {
    const lastIndex = colonSegments.length - 1;
    return [{ delimiter: ':', index: lastIndex, part: colonSegments[lastIndex] }];
  }

  // A code stuck to a number ("4HDC 3,700"): a numeric field gets the
  // number. Splitting on every separator would find "700" after the comma
  // first, so the pair is read as a whole. Any other field only gets the
  // code when something confirms it, like any other part: in a text field
  // the space may belong to the value ("INV-2024 0012").
  const pair = field.numeric ? findCodeNumberPair(value) : null;
  if (pair) {
    return [{ delimiter: pair.delimiter, index: pair.number.index, part: pair.number.part }];
  }

  return confirmedParts(value, confirmation(suggestion, others, field, value.trim()), field);
}

/** A text value from a snippet, that isn't a number, date or date format. */
const isSplittable = (suggestion: FieldSuggestion) => {
  const { value, snippet } = suggestion;
  if (suggestion.iterator === true || typeof value !== 'string') return false;
  if (snippet === undefined || snippet === null || snippet === '') return false;
  return !(
    looksLikeFormattedNumber(value) ||
    looksLikeDateOrTime(value) ||
    looksLikeDateFormatPattern(value)
  );
};

/**
 * The parts of the value, split on every separator in turn, that are
 * confirmed — each part once, from the first separator that finds it.
 */
const confirmedParts = (
  value: string,
  confirmed: (part: string) => boolean,
  field: FieldExpectation,
): SplitCandidate[] => {
  const candidates: SplitCandidate[] = [];
  const offered = new Set<string>();
  for (const delimiter of SEPARATORS) {
    const parts = splitParts(value, delimiter) ?? [];
    const onlyNumber = field.numeric ? soleNumber(parts) : undefined;
    for (const { index, part } of parts) {
      if (offered.has(part) || !(confirmed(part) || part === onlyNumber)) continue;
      candidates.push({ delimiter, index, part });
      offered.add(part);
    }
  }
  return candidates;
};

/** The one plain number among the parts, if there is exactly one. */
const soleNumber = (parts: SplitPart[]): string | undefined => {
  const numbers = parts.filter(({ part }) => isPlainNumber(part));
  return numbers.length === 1 ? numbers[0].part : undefined;
};

export const splitStep: TransformStep = {
  name: 'split',
  stage: 'split',
  mode: 'add',
  apply: (state, { entry, suggestion, suggestions }) => {
    const current = {
      ...suggestion,
      value: state.value,
      snippet: state.snippet as FieldSuggestion['snippet'],
    };
    const inner = isWrappedSnippet(state.snippet)
      ? (state.snippet as unknown[][])[0][0]
      : state.snippet;

    return findSplitCandidates(current, suggestions, fieldExpectation(entry)).map(({ delimiter, index, part }) => ({
      snippet: buildSplitSnippet(inner, delimiter, index),
      value: part,
    }));
  },
};

export const splitHandling = definePlugin<{}>({
  name: 'Split',
  version: '0.6.0',
  description:
    'Replaces the Split transformer: takes one part of a value, between separators, when something confirms it.',
  initialState: {},

  bootstrap: () => {},

  transformSteps: () => [splitStep],
});
